// Test Suite — questions through the real assistants, answers checked against
// the client's dashboard. A full-screen view of its own (own look and tokens
// in testsuite.css), opened from the admin console by super admins. Runs on
// the instance the console points at (useConsole().client): answers and
// dashboard figures stay on that instance.
//
// Views: Übersicht (matrix of the selected run, start a run) · Fragen
// (questions, editor, import of a boss report) · Einrichtung (test profiles,
// dashboard logins) · Läufe (history).
import { useCallback, useEffect, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faXmark, faFlaskVial, faTableCells, faListCheck, faSliders, faClockRotateLeft, faPlay, faStop, faArrowsRotate,
  faCircleCheck, faCircleXmark, faCircleExclamation, faCircleMinus, faChevronDown, faChevronRight, faDatabase,
} from '@fortawesome/free-solid-svg-icons';
import { useConsole } from '../admin/context';
import CasesView from './CasesView';
import SetupView from './SetupView';
import { ASSISTANT, CHECK_LABEL, SCOPE, STATUS, applies, combine, expectedText, seconds, when } from './format';
import './testsuite.css';

const VIEWS = [
  { key: 'overview', label: 'Übersicht', icon: faTableCells },
  { key: 'cases', label: 'Fragen', icon: faListCheck },
  { key: 'setup', label: 'Einrichtung', icon: faSliders },
  { key: 'runs', label: 'Läufe', icon: faClockRotateLeft },
];

const ICON = { pass: faCircleCheck, fail: faCircleXmark, flaky: faCircleXmark, error: faCircleExclamation, skipped: faCircleMinus };

// The language model a run used (selection.model); empty = the instance default.
function modelOf(data, run) {
  const value = run?.selection?.model;
  if (!value) return 'Standardmodell';
  const m = (data.models || []).find((x) => x.value === value);
  return m ? `${m.label} · ${m.provider}` : value;
}

function Verdict({ status, passed, total }) {
  if (!status || status === 'none') return <span className="ts-cell none" />;
  return (
    <span className={`ts-cell s-${status}`} title={STATUS[status]?.label || status}>
      <FontAwesomeIcon icon={ICON[status] || faCircleMinus} />
      {total > 1 && <small>{passed}/{total}</small>}
    </span>
  );
}

// ── Start a run ─────────────────────────────────────────────────────────────
function RunLauncher({ data, onStart, preset, onClose }) {
  const profiles = (data.profiles || []).filter((p) => p.active);
  const [assistants, setAssistants] = useState(preset?.assistants || []);
  const [ids, setIds] = useState(preset?.profile_ids || profiles.map((p) => p.id));
  const [repeats, setRepeats] = useState(1);
  const [model, setModel] = useState(preset?.model || '');
  const caseIds = preset?.case_ids || null;
  const count = useMemo(() => {
    const cases = (data.cases || []).filter((c) => c.active && (!caseIds || caseIds.includes(c.id))
      && (!assistants.length || assistants.includes(c.assistant)));
    const chosen = profiles.filter((p) => ids.includes(p.id) && (!assistants.length || assistants.includes(p.assistant)));
    return cases.reduce((n, c) => n + chosen.filter((p) => applies(c, p)).length, 0) * repeats;
  }, [data.cases, profiles, ids, assistants, repeats, caseIds]);
  const toggle = (list, setList, v) => setList(list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);
  return (
    <div className="ts-modal-bg" onClick={onClose}>
      <div className="ts-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ts-modal-head">
          <h3><FontAwesomeIcon icon={faPlay} /> Testlauf starten</h3>
          <button type="button" className="ts-icon-btn" onClick={onClose}><FontAwesomeIcon icon={faXmark} /></button>
        </div>
        {caseIds && <p className="ts-lead">Nur {caseIds.length === 1 ? 'diese Frage' : `${caseIds.length} Fragen`}.</p>}
        <div className="ts-mini">Assistenten</div>
        <div className="ts-toggles">
          {['efd', 'ppi'].map((a) => (
            <button key={a} type="button" className={assistants.includes(a) ? 'on' : ''}
              onClick={() => toggle(assistants, setAssistants, a)}>{ASSISTANT[a]}</button>
          ))}
          <span className="ts-toggles-note">{assistants.length ? '' : 'beide'}</span>
        </div>
        <div className="ts-mini">Profile</div>
        <div className="ts-pick">
          {profiles.map((p) => (
            <label key={p.id} className={`ts-check-inline ${assistants.length && !assistants.includes(p.assistant) ? 'dim' : ''}`}>
              <input type="checkbox" checked={ids.includes(p.id)} onChange={() => toggle(ids, setIds, p.id)} /> {p.label}
            </label>
          ))}
        </div>
        <div className="ts-row">
          <label className="ts-field"><span>Wiederholungen je Frage</span>
            <select value={repeats} onChange={(e) => setRepeats(Number(e.target.value))}>
              {[1, 2, 3].map((n) => <option key={n} value={n}>{n}×{n > 1 ? ' — zeigt schwankende Antworten' : ''}</option>)}
            </select></label>
          <label className="ts-field"><span>Sprachmodell</span>
            <select value={model} onChange={(e) => setModel(e.target.value)}>
              <option value="">Standard der Instanz</option>
              {(data.models || []).map((m) => (
                <option key={m.value} value={m.value} disabled={!m.available}>
                  {m.label} · {m.provider}{m.available ? '' : ' (kein API-Key)'}
                </option>
              ))}
            </select></label>
        </div>
        <div className="ts-modal-foot">
          <span className="ts-muted">{count} Antworten · echte LLM-Aufrufe (≈ {Math.max(1, Math.round(count * 15 / 3 / 60))} min)</span>
          <button type="button" className="ts-btn" disabled={!count}
            onClick={() => onStart({ assistants, profile_ids: ids, case_ids: caseIds || [], repeats, model })}>
            <FontAwesomeIcon icon={faPlay} /> Starten
          </button>
        </div>
      </div>
    </div>
  );
}

// ── One result ──────────────────────────────────────────────────────────────
function ResultDrawer({ client, resultId, caseObj, profile, onClose }) {
  const [r, setR] = useState(null);
  const [error, setError] = useState('');
  const [open, setOpen] = useState({});
  useEffect(() => {
    let alive = true;
    client.get(`/api/admin/tests/results/${resultId}`).then((x) => alive && setR(x)).catch((e) => alive && setError(e.message));
    return () => { alive = false; };
  }, [client, resultId]);
  return (
    <div className="ts-drawer-bg" onClick={onClose}>
      <aside className="ts-drawer" onClick={(e) => e.stopPropagation()}>
        <div className="ts-drawer-head">
          <div>
            <div className="ts-mini">{profile?.label}</div>
            <h3>{caseObj?.question || r?.question}</h3>
            {caseObj?.origin && <div className="ts-muted">{caseObj.origin}</div>}
          </div>
          <button type="button" className="ts-icon-btn" onClick={onClose}><FontAwesomeIcon icon={faXmark} /></button>
        </div>
        {error && <div className="ts-warn">{error}</div>}
        {!r && !error && <div className="ts-empty">Lädt …</div>}
        {r && (
          <>
            <div className={`ts-verdict s-${r.status}`}>
              <FontAwesomeIcon icon={ICON[r.status] || faCircleMinus} />
              <strong>{STATUS[r.status]?.label}</strong>
              <span>{seconds(r.duration_ms)}{r.tokens ? ` · ${r.tokens.toLocaleString('de-CH')} Tokens` : ''}{r.attempt > 1 ? ` · Versuch ${r.attempt}` : ''}</span>
            </div>
            {r.error && <pre className="ts-error">{r.error}</pre>}
            {!!(r.checks || []).length && (
              <table className="ts-checks-table">
                <thead><tr><th /><th>Prüfung</th><th>Erwartet</th><th>Befund</th></tr></thead>
                <tbody>
                  {r.checks.map((c, i) => (
                    <tr key={i} className={c.ok === true ? 'ok' : c.ok === false ? 'bad' : 'na'}>
                      <td><FontAwesomeIcon icon={c.ok === true ? faCircleCheck : c.ok === false ? faCircleXmark : faCircleMinus} /></td>
                      <td><strong>{c.label}</strong><div className="ts-muted">{CHECK_LABEL[c.type]}</div></td>
                      <td className="mono">{expectedText(c)}{c.source && <div className="ts-source">{c.source}</div>}</td>
                      <td>{c.detail || (c.ok ? 'in der Antwort' : '')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <div className="ts-mini">Antwort von Bro</div>
            <div className="ts-answer"><ReactMarkdown remarkPlugins={[remarkGfm]}>{r.answer || '—'}</ReactMarkdown></div>
            {!!(r.tools || []).length && (
              <>
                <div className="ts-mini">Werkzeugaufrufe ({r.tools.length})</div>
                {r.tools.map((t, i) => (
                  <div key={i} className="ts-tool">
                    <button type="button" onClick={() => setOpen({ ...open, [i]: !open[i] })}>
                      <FontAwesomeIcon icon={open[i] ? faChevronDown : faChevronRight} /> <strong>{t.tool}</strong>
                      <span className="ts-muted">{typeof t.arguments === 'object' && t.arguments?.sql ? t.arguments.sql.replace(/\s+/g, ' ').slice(0, 90) : ''}</span>
                    </button>
                    {open[i] && (
                      <div className="ts-tool-body">
                        <pre>{typeof t.arguments === 'object' ? (t.arguments.sql || JSON.stringify(t.arguments, null, 2)) : String(t.arguments)}</pre>
                        <div className="ts-mini">Ergebnis{t.truncated ? ' (gekürzt)' : ''}</div>
                        <pre>{t.result}</pre>
                      </div>
                    )}
                  </div>
                ))}
              </>
            )}
          </>
        )}
      </aside>
    </div>
  );
}

// ── Overview: the matrix of one run ─────────────────────────────────────────
function Overview({ data, run, onOpen, onLaunch, onCancel, activeRun }) {
  const profiles = (data.profiles || []).filter((p) => p.active);
  const cases = (data.cases || []).filter((c) => c.active);
  const byPair = useMemo(() => {
    const m = {};
    (run?.results || []).forEach((r) => { (m[`${r.case_id}:${r.profile_id}`] ||= []).push(r); });
    return m;
  }, [run]);
  const totals = run?.run?.totals || {};
  const running = run?.run?.status === 'running';
  const pct = totals.total ? Math.round((totals.done || 0) / totals.total * 100) : 0;
  const groups = ['efd', 'ppi'].map((a) => ({
    assistant: a, profiles: profiles.filter((p) => p.assistant === a), cases: cases.filter((c) => c.assistant === a),
  })).filter((g) => g.cases.length && g.profiles.length);

  return (
    <div className="ts-pane">
      <div className="ts-hero">
        <div>
          <div className="ts-mini">{run ? `Lauf #${run.run.id} · ${when(run.run.started_at)} · ${run.run.started_by} · ${modelOf(data, run.run)}` : 'Noch kein Lauf'}</div>
          <div className="ts-score">
            <span className="s-pass"><b>{totals.pass || 0}</b> bestanden</span>
            <span className="s-fail"><b>{totals.fail || 0}</b> Abweichungen</span>
            <span className="s-skipped"><b>{totals.skipped || 0}</b> nicht bewertet</span>
            <span className="s-error"><b>{totals.error || 0}</b> Fehler</span>
          </div>
          {running && (
            <div className="ts-progress"><div style={{ width: `${pct}%` }} /><span>{totals.done || 0} von {totals.total} · {pct}%</span></div>
          )}
        </div>
        <div className="ts-hero-actions">
          {activeRun ? (
            <button type="button" className="ts-btn danger" onClick={onCancel}><FontAwesomeIcon icon={faStop} /> Abbrechen</button>
          ) : (
            <button type="button" className="ts-btn big" onClick={() => onLaunch(null)}><FontAwesomeIcon icon={faPlay} /> Testlauf starten</button>
          )}
        </div>
      </div>

      {!groups.length && (
        <div className="ts-empty big">
          Noch nichts zu testen. Unter <b>Einrichtung</b> die Standardprofile anlegen; die Startfragen sind schon da.
        </div>
      )}

      {groups.map((g) => (
        <section key={g.assistant} className="ts-matrix-wrap">
          <h2 className="ts-h2">{ASSISTANT[g.assistant]}</h2>
          <div className="ts-matrix" style={{ gridTemplateColumns: `minmax(280px, 1fr) repeat(${g.profiles.length}, 92px)` }}>
            <div className="ts-mh corner" />
            {g.profiles.map((p) => (
              <div key={p.id} className="ts-mh" title={p.label}>
                <span>{p.data_set_name}</span>
                <b>{SCOPE[p.scope_type]}</b>
                {p.scope_value && <small>{p.scope_value}</small>}
              </div>
            ))}
            {g.cases.map((c) => {
              const prev = run?.previous || {};
              return [
                <div key={`q${c.id}`} className="ts-mq" title={c.question}>
                  <span className="ts-mq-text">{c.question}</span>
                  {c.origin && <span className="ts-muted">{c.origin}</span>}
                </div>,
                ...g.profiles.map((p) => {
                  if (!applies(c, p)) return <div key={`${c.id}:${p.id}`} className="ts-mc na" />;
                  const results = byPair[`${c.id}:${p.id}`] || [];
                  const agg = combine(results);
                  const wasOk = prev[`${c.id}:${p.id}`] === 'pass';
                  const newlyRed = wasOk && ['fail', 'flaky', 'error'].includes(agg.status);
                  return (
                    <button key={`${c.id}:${p.id}`} type="button" className={`ts-mc ${newlyRed ? 'newly' : ''}`}
                      disabled={!results.length} onClick={() => onOpen(results[results.length - 1], c, p)}>
                      <Verdict {...agg} />
                      {newlyRed && <i className="ts-new">neu</i>}
                    </button>
                  );
                }),
              ];
            })}
          </div>
        </section>
      ))}
    </div>
  );
}

function RunsView({ data, onPick, selectedId }) {
  return (
    <div className="ts-pane">
      <h2 className="ts-h2">Läufe</h2>
      <div className="ts-runs">
        {(data.runs || []).map((r) => {
          const t = r.totals || {};
          return (
            <button key={r.id} type="button" className={`ts-run ${selectedId === r.id ? 'on' : ''}`} onClick={() => onPick(r.id)}>
              <span className="ts-run-id">#{r.id}</span>
              <span>{when(r.started_at)}</span>
              <span className="ts-muted">{r.started_by}</span>
              <span className="ts-muted">{modelOf(data, r)}</span>
              <span className={`ts-pill ${r.status}`}>{r.status === 'running' ? 'läuft' : r.status === 'aborted' ? 'abgebrochen' : 'fertig'}</span>
              <span className="ts-run-bar">
                {['pass', 'fail', 'skipped', 'error'].map((s) => (t[s] ? <i key={s} className={`s-${s}`} style={{ flex: t[s] }} /> : null))}
              </span>
              <span className="mono">{t.pass || 0}/{t.total || 0}</span>
            </button>
          );
        })}
        {!(data.runs || []).length && <div className="ts-empty">Noch keine Läufe.</div>}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
export default function TestSuite({ onClose }) {
  const { client } = useConsole();
  const [view, setView] = useState('overview');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [runId, setRunId] = useState(null);
  const [run, setRun] = useState(null);
  const [launch, setLaunch] = useState(undefined);          // undefined = closed, null = all, {...} = preset
  const [drawer, setDrawer] = useState(null);
  const [toast, setToast] = useState(null);

  const flash = useCallback((message, bad) => {
    setToast({ message, bad });
    setTimeout(() => setToast(null), bad ? 6000 : 3000);
  }, []);

  const load = useCallback(async () => {
    try {
      const d = await client.get('/api/admin/tests/overview');
      setData(d);
      setError('');
      setRunId((cur) => cur ?? d.active_run ?? d.runs?.[0]?.id ?? null);
    } catch (e) {
      setError(e.message);
    }
  }, [client]);

  const loadRun = useCallback(async (id) => {
    if (!id) { setRun(null); return; }
    try { setRun(await client.get(`/api/admin/tests/runs/${id}`)); } catch (e) { setError(e.message); }
  }, [client]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadRun(runId); }, [runId, loadRun]);
  const activeRun = data?.active_run || (run?.run?.status === 'running' ? run.run.id : null);
  useEffect(() => {
    if (!activeRun) return undefined;
    const id = setInterval(() => { loadRun(runId); if (!document.hidden) load(); }, 3000);
    return () => clearInterval(id);
  }, [activeRun, runId, load, loadRun]);

  const start = async (selection) => {
    try {
      const r = await client.post('/api/admin/tests/runs', { selection });
      setLaunch(undefined);
      setRunId(r.run_id);
      setView('overview');
      flash('Testlauf gestartet.');
      load();
    } catch (e) {
      flash(e.message, true);
    }
  };
  const cancel = async () => {
    try { await client.post(`/api/admin/tests/runs/${activeRun}/cancel`); flash('Wird nach den laufenden Fragen abgebrochen.'); }
    catch (e) { flash(e.message, true); }
  };

  const lastStatus = useCallback((caseId) => {
    const rs = (run?.results || []).filter((r) => r.case_id === caseId);
    return combine(rs).status;
  }, [run]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') { if (drawer) setDrawer(null); else if (launch !== undefined) setLaunch(undefined); else onClose(); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawer, launch, onClose]);

  return (
    <div className="ts-root" role="dialog" aria-label="Test Suite">
      <header className="ts-top">
        <div className="ts-brand">
          <span className="ts-logo"><FontAwesomeIcon icon={faFlaskVial} /></span>
          <div>
            <strong>Test Suite</strong>
            <span>Bro gegen das Dashboard · <FontAwesomeIcon icon={faDatabase} /> {client.target?.remote ? client.target.label : 'diese Instanz'}</span>
          </div>
        </div>
        <nav className="ts-nav">
          {VIEWS.map((v) => (
            <button key={v.key} type="button" className={view === v.key ? 'on' : ''} onClick={() => setView(v.key)}>
              <FontAwesomeIcon icon={v.icon} /> {v.label}
            </button>
          ))}
        </nav>
        <div className="ts-top-right">
          {activeRun && <span className="ts-live"><i /> Lauf #{activeRun} läuft</span>}
          <button type="button" className="ts-icon-btn" title="Neu laden" onClick={() => { load(); loadRun(runId); }}><FontAwesomeIcon icon={faArrowsRotate} /></button>
          <button type="button" className="ts-icon-btn" title="Schliessen (Esc)" onClick={onClose}><FontAwesomeIcon icon={faXmark} /></button>
        </div>
      </header>
      <main className="ts-main">
        {error && <div className="ts-warn">{error}</div>}
        {!data && !error && <div className="ts-empty big">Lädt …</div>}
        {data && view === 'overview' && (
          <Overview data={data} run={run} activeRun={activeRun} onLaunch={(preset) => setLaunch(preset)} onCancel={cancel}
            onOpen={(result, c, p) => setDrawer({ id: result.id, c, p })} />
        )}
        {data && view === 'cases' && (
          <CasesView client={client} data={data} reload={load} flash={flash} lastStatus={lastStatus}
            onRun={(preset) => setLaunch(preset)} />
        )}
        {data && view === 'setup' && <SetupView client={client} data={data} reload={load} flash={flash} />}
        {data && view === 'runs' && <RunsView data={data} selectedId={runId} onPick={(id) => { setRunId(id); setView('overview'); }} />}
      </main>
      {launch !== undefined && data && <RunLauncher data={data} preset={launch} onStart={start} onClose={() => setLaunch(undefined)} />}
      {drawer && <ResultDrawer client={client} resultId={drawer.id} caseObj={drawer.c} profile={drawer.p} onClose={() => setDrawer(null)} />}
      {toast && <div className={`ts-toast ${toast.bad ? 'bad' : ''}`}>{toast.message}</div>}
    </div>
  );
}
